import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type MutableRefObject,
} from "react";
import {
  Outlet,
  useLocation,
  useNavigate,
  type Location,
} from "react-router-dom";
import { useUserMe } from "@/lib/user-me";
import { OnboardingRail, type RailStep } from "./_OnboardingRail";

/**
 * Onboarding shell — Notion / Linear sidebar layout.
 *
 *   ┌─────────────────┬─────────────────────────────────────┐
 *   │ ▣ Booqly        │  STEP 2 OF 5                         │
 *   │                 │  Set up your workspace               │
 *   │ ✦ Get started   │  ...                                 │
 *   │ ─────────────── │                                      │
 *   │ ✓ About you     │  [step body]                         │
 *   │ ● Your workspace│                                      │
 *   │   Invite team   │  ── divider ──                       │
 *   │   Pick services │  ← Back        Skip   Continue →     │
 *   │   Choose plan   │                                      │
 *   │ ─────────────── │                                      │
 *   │ ✓ All set       │                                      │
 *   │ liam@acme.test  │                                      │
 *   │ Sign out        │                                      │
 *   └─────────────────┴─────────────────────────────────────┘
 *
 * Step contract:
 *   - Form steps call `useRegisterStep({ onContinue, disabled,
 *     continueLabel })` to plug the validation + advance logic.
 *   - They also call `useOnboardingFooter()` to render the
 *     back/skip/continue row inside their own StepShell so the
 *     footer's max-width matches the step's variant (form 640 vs
 *     wide 880 for card grids).
 *   - Welcome and Done don't use either — they render their own
 *     inline CTA at the end of their content.
 *
 * The shell handles navigation, route → step lookup, and the
 * pending/disabled/continueLabel store. It's intentionally
 * chrome-light — just rail + outlet — so steps drive their own
 * vertical rhythm.
 */

export type FormStepKey =
  | "personal"
  | "workspace"
  | "invite"
  | "services"
  | "plan";

type StepInfo = {
  key: FormStepKey;
  label: string;
  subLabel: string;
  required: boolean;
};

const ALL_STEPS: StepInfo[] = [
  { key: "personal",  label: "About you",      subLabel: "Your name + photo",     required: true  },
  { key: "workspace", label: "Your workspace", subLabel: "Name and brand",        required: true  },
  { key: "invite",    label: "Invite team",    subLabel: "Add teammates (later)", required: false },
  { key: "services",  label: "Pick services",  subLabel: "Starter pack",          required: false },
  { key: "plan",      label: "Choose plan",    subLabel: "Pro — free",            required: false },
];

/**
 * Skip the Personal step when the user already has a first name.
 * (E.g. an existing operator creating an Nth workspace from the
 * dashboard "Create workspace" dialog — identity is already set.)
 */
function stepsForUser(userHasName: boolean): StepInfo[] {
  return userHasName
    ? ALL_STEPS.filter((s) => s.key !== "personal")
    : ALL_STEPS;
}

/* ---------------- step-control context ---------------- */

type ControlContext = {
  onContinueRef: MutableRefObject<(() => void | Promise<void>) | undefined>;
  setDisabled: (b: boolean) => void;
  setContinueLabel: (s: string | undefined) => void;
};

type FooterContext = {
  /** Callbacks the StepFooter component invokes. */
  onBack: () => void;
  onSkip: () => void;
  onContinue: () => void;
  /** UI hints. */
  showBack: boolean;
  showSkip: boolean;
  pending: boolean;
  disabled: boolean;
  continueLabel: string;
};

const Ctx = createContext<ControlContext | null>(null);
const FooterCtx = createContext<FooterContext | null>(null);

export function useRegisterStep(opts: {
  onContinue?: () => void | Promise<void>;
  disabled?: boolean;
  continueLabel?: string;
}) {
  const ctx = useContext(Ctx);
  if (!ctx) {
    throw new Error("useRegisterStep must be used inside <OnboardingShell />");
  }
  ctx.onContinueRef.current = opts.onContinue;

  useEffect(() => {
    ctx.setDisabled(!!opts.disabled);
  }, [ctx, opts.disabled]);
  useEffect(() => {
    ctx.setContinueLabel(opts.continueLabel);
  }, [ctx, opts.continueLabel]);
}

/** Form steps consume this to render the StepFooter inline. */
export function useOnboardingFooter(): FooterContext {
  const ctx = useContext(FooterCtx);
  if (!ctx) {
    throw new Error("useOnboardingFooter must be used inside <OnboardingShell />");
  }
  return ctx;
}

/* ---------------- shell ---------------- */

export function OnboardingShell() {
  const navigate = useNavigate();
  const loc = useLocation();
  const userMe = useUserMe();

  const onContinueRef = useRef<(() => void | Promise<void>) | undefined>(
    undefined,
  );
  const [disabled, setDisabled] = useState(false);
  const [continueLabel, setContinueLabel] = useState<string | undefined>();
  const [pending, setPending] = useState(false);

  const ctx = useRef<ControlContext>({
    onContinueRef,
    setDisabled,
    setContinueLabel,
  }).current;

  const userHasName = !!userMe.data?.firstName?.trim();
  const steps = stepsForUser(userHasName);

  const slug = slugFromLocation(loc);
  const isWelcome = slug === "welcome";
  const isDone = slug === "done";
  const isFormStep = !isWelcome && !isDone;

  const currentIndex = isFormStep
    ? Math.max(0, steps.findIndex((s) => s.key === slug))
    : 0;
  const currentStep = isFormStep ? steps[currentIndex] : null;
  const isLast = currentIndex === steps.length - 1;

  useEffect(() => {
    setDisabled(false);
    setContinueLabel(undefined);
  }, [slug]);

  const goTo = (idx: number) => {
    const next = steps[Math.max(0, Math.min(steps.length - 1, idx))];
    navigate(`/onboarding/${next.key}`, { replace: true });
  };

  const handleContinue = async () => {
    const fn = onContinueRef.current;
    if (!fn && isLast) {
      console.warn("[onboarding] missing onContinue handler on last step");
    }
    if (fn) {
      setPending(true);
      try {
        await fn();
      } catch {
        setPending(false);
        return;
      }
      setPending(false);
    }
    if (!isLast) goTo(currentIndex + 1);
  };

  const handleSkip = () => {
    if (!isLast) goTo(currentIndex + 1);
  };

  const handleBack = () => {
    if (currentIndex > 0) goTo(currentIndex - 1);
  };

  const footerCtx: FooterContext = {
    onBack: handleBack,
    onSkip: handleSkip,
    onContinue: handleContinue,
    showBack: currentIndex > 0,
    showSkip: !!currentStep && !currentStep.required,
    pending,
    disabled,
    continueLabel:
      continueLabel ?? (isLast ? "Finish setup" : "Continue"),
  };

  const railSteps: RailStep[] = steps.map((s) => ({
    key: s.key,
    label: s.label,
    subLabel: s.subLabel,
  }));

  return (
    <Ctx.Provider value={ctx}>
      <FooterCtx.Provider value={footerCtx}>
        <div className="min-h-screen flex bg-background">
          {/* Rail (desktop) — collapses below md */}
          <div className="hidden md:block">
            <OnboardingRail
              steps={railSteps}
              currentIndex={currentIndex}
              isWelcome={isWelcome}
              isDone={isDone}
              onJump={goTo}
            />
          </div>

          {/* Content column */}
          <main className="flex-1 min-w-0 flex flex-col">
            {/* Mobile top bar showing brand + step counter */}
            <div className="md:hidden border-b border-border bg-background px-5 h-14 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="h-7 w-7 rounded bg-primary text-primary-foreground inline-flex items-center justify-center text-xs font-bold">
                  B
                </div>
                <span className="text-sm font-semibold tracking-tight">Booqly</span>
              </div>
              {isFormStep && currentStep && (
                <span className="text-xs text-muted-foreground">
                  Step {currentIndex + 1} of {steps.length}
                </span>
              )}
            </div>

            <Outlet />
          </main>
        </div>
      </FooterCtx.Provider>
    </Ctx.Provider>
  );
}

function slugFromLocation(loc: Location): string {
  return loc.pathname.split("/").pop() ?? "";
}
